import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Layout from "../components/Layout";
import api from "../lib/api";
import { Button } from "../components/ui/button";
import { toast, Toaster } from "sonner";
import { FileClock, ArrowRight, Trash2, User, Phone } from "lucide-react";

const DRAFT_PREFIX = "draft-visit-";

/**
 * Scan localStorage for draft-visit-<pid> keys, parse them, and enrich with patient info.
 * Returns { pid, savedAt, state, patient } records sorted newest-first.
 */
export function listDrafts() {
  const out = [];
  try {
    for (let i = 0; i < localStorage.length; i++) {
      const k = localStorage.key(i);
      if (!k || !k.startsWith(DRAFT_PREFIX)) continue;
      const pid = k.slice(DRAFT_PREFIX.length);
      const raw = localStorage.getItem(k);
      if (!raw) continue;
      try {
        const d = JSON.parse(raw);
        if (d && d.state) out.push({ pid, savedAt: d.savedAt, state: d.state });
      } catch (e) { console.debug("[silent]", e); }
    }
  } catch (e) { console.debug("[silent]", e); }
  return out.sort((a, b) => (b.savedAt || 0) - (a.savedAt || 0));
}

function summarise(state) {
  if (!state) return "Empty draft";
  const bits = [];
  if (state.chief) bits.push(`Complaints: ${state.chief.slice(0, 60)}`);
  if (state.diagnosis?.primary) bits.push(`Dx: ${state.diagnosis.primary}`);
  const meds = (state.medicines || []).filter((m) => m.brand_name);
  if (meds.length) bits.push(`${meds.length} medicine(s)`);
  const invs = (state.investigations || []).filter((i) => i.test_name);
  if (invs.length) bits.push(`${invs.length} investigation(s)`);
  const v = state.vitals || {};
  if (v.sbp || v.dbp) bits.push(`BP ${v.sbp || "-"}/${v.dbp || "-"}`);
  return bits.length ? bits.join(" · ") : "Just opened — nothing typed yet";
}

export default function Drafts() {
  const [drafts, setDrafts] = useState([]);
  const [loading, setLoading] = useState(true);
  const nav = useNavigate();

  const load = async () => {
    setLoading(true);
    let serverRows = [];
    try {
      const r = await api.get("/visit-drafts");
      serverRows = (r.data.drafts || []).map((d) => ({
        pid: d.patient_id,
        savedAt: d.saved_at ? new Date(d.saved_at).getTime() : 0,
        state: d.state,
        patient: d.patient || null,
        cloud: true,
        savedBy: d.saved_by,
        waitingFor: d.waiting_for || "",
        reportsReady: !!d.reports_ready,
      }));
    } catch (e) { console.debug("[silent]", e); }
    const serverPids = new Set(serverRows.map((d) => d.pid));
    // Local-only drafts (this device, not yet synced) — still shown, flagged.
    const localOnly = listDrafts().filter((d) => !serverPids.has(d.pid)).map((d) => ({ ...d, cloud: false }));
    const enrichedLocal = await Promise.all(
      localOnly.map(async (r) => {
        try {
          const p = await api.get(`/patients/${r.pid}`);
          return { ...r, patient: p.data.patient };
        } catch {
          return { ...r, patient: null };
        }
      })
    );
    setDrafts([...serverRows, ...enrichedLocal].sort((a, b) => (b.savedAt || 0) - (a.savedAt || 0)));
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const discard = async (pid) => {
    if (!confirm("Discard this draft? This cannot be undone.")) return;
    try { localStorage.removeItem(DRAFT_PREFIX + pid); } catch (e) { console.debug("[silent]", e); }
    try { await api.delete(`/visit-drafts/${pid}`); } catch (e) { console.debug("[silent]", e); }
    toast.info("Draft discarded");
    setDrafts((prev) => prev.filter((d) => d.pid !== pid));
    // notify Layout to refresh badge
    window.dispatchEvent(new Event("drafts-changed"));
  };

  const clearAll = async () => {
    if (!confirm(`Discard all ${drafts.length} draft(s)?`)) return;
    for (const d of drafts) {
      try { localStorage.removeItem(DRAFT_PREFIX + d.pid); } catch (e) { console.debug("[silent]", e); }
      try { await api.delete(`/visit-drafts/${d.pid}`); } catch (e) { console.debug("[silent]", e); }
    }
    setDrafts([]);
    toast.info("All drafts discarded");
    window.dispatchEvent(new Event("drafts-changed"));
  };

  return (
    <Layout>
      <Toaster position="top-right" richColors />
      <div className="flex items-start justify-between">
        <div>
          <div className="overline">WORK IN PROGRESS</div>
          <h1 className="font-heading text-3xl font-semibold mt-1 text-slate-900 flex items-center gap-3">
            <FileClock size={26} className="text-pink-600" />
            Open drafts
          </h1>
          <p className="mt-2 text-sm text-slate-600 max-w-lg">
            Every visit form autosaves to the cloud as you type — start an entry on the clinic PC in the
            morning (vitals, complaints), tap <b>Draft</b>, and finish it from any PC when the lab reports
            arrive. Drafts auto-expire after 30 days.
          </p>
        </div>
        {drafts.length > 0 && (
          <Button variant="outline" onClick={clearAll} className="gap-2 text-red-600 border-red-200 hover:bg-red-50" data-testid="clear-all-drafts">
            <Trash2 size={14} /> Discard all
          </Button>
        )}
      </div>

      <div className="mt-6 space-y-3" data-testid="drafts-list">
        {loading && <div className="text-sm text-slate-500">Scanning local drafts…</div>}
        {!loading && drafts.length === 0 && (
          <div className="border border-dashed border-slate-300 rounded-md p-12 text-center bg-white">
            <FileClock size={28} className="text-slate-300 mx-auto" />
            <div className="mt-3 text-sm font-medium text-slate-700">No open drafts</div>
            <div className="mt-1 text-xs text-slate-500">Start typing on any patient's visit and it will appear here automatically.</div>
          </div>
        )}
        {drafts.map((d) => (
          <div
            key={d.pid}
            className="bg-white border border-slate-200 rounded-md p-4 flex items-center justify-between hover:border-pink-300 transition-colors"
            data-testid={`draft-card-${d.pid}`}
          >
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-3">
                <div className="h-9 w-9 rounded bg-pink-100 flex items-center justify-center text-pink-700">
                  <User size={16} />
                </div>
                <div className="min-w-0">
                  <div className="font-medium text-slate-900 truncate flex items-center gap-2">
                    {d.patient?.name || <span className="italic text-slate-400">Unknown patient</span>}
                    <span className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold tabular">{d.pid}</span>
                    {d.cloud ? (
                      <span className="text-[9px] uppercase tracking-wide px-1.5 py-0.5 rounded bg-emerald-50 text-emerald-700 border border-emerald-200" data-testid={`draft-cloud-${d.pid}`}>Cloud · all PCs</span>
                    ) : (
                      <span className="text-[9px] uppercase tracking-wide px-1.5 py-0.5 rounded bg-amber-50 text-amber-700 border border-amber-200" data-testid={`draft-local-${d.pid}`}>This device only</span>
                    )}
                    {d.reportsReady && (
                      <span className="text-[9px] uppercase tracking-wide px-1.5 py-0.5 rounded bg-green-600 text-white font-semibold animate-pulse" data-testid={`draft-ready-${d.pid}`}>Reports arrived — ready to finish</span>
                    )}
                  </div>
                  {d.waitingFor && (
                    <div className="text-[11px] text-amber-700 mt-0.5 flex items-center gap-1" data-testid={`draft-waiting-${d.pid}`}>
                      <FileClock size={11} /> Waiting for: <span className="font-medium">{d.waitingFor}</span>
                    </div>
                  )}
                  <div className="text-xs text-slate-500 flex items-center gap-3 mt-0.5">
                    {d.patient?.mobile && <span className="flex items-center gap-1"><Phone size={11} /> {d.patient.mobile}</span>}
                    <span className="tabular">
                      Saved {d.savedAt ? new Date(d.savedAt).toLocaleString(undefined, { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }) : "—"}
                    </span>
                  </div>
                </div>
              </div>
              <div className="mt-2 ml-12 text-xs text-slate-600 leading-relaxed">{summarise(d.state)}</div>
            </div>
            <div className="flex items-center gap-2 pl-4">
              <button
                onClick={() => discard(d.pid)}
                className="p-2 rounded text-red-600 hover:bg-red-50"
                title="Discard draft"
                data-testid={`discard-draft-${d.pid}`}
              >
                <Trash2 size={14} />
              </button>
              <Button
                onClick={() => nav(`/patient/${d.pid}`)}
                className="bg-pink-600 hover:bg-pink-700 text-white h-9 gap-1"
                data-testid={`resume-draft-${d.pid}`}
              >
                Resume <ArrowRight size={14} />
              </Button>
            </div>
          </div>
        ))}
      </div>
    </Layout>
  );
}
